// ================================================================
// src/core/education/curriculum.js
// ================================================================
// سیستم یکپارچه‌ی مقاطع، رشته‌ها و کتاب‌های درسی
// ----------------------------------------------------------------
// تنها منبع حقیقت (Single Source of Truth) برای:
//   • مقاطع تحصیلی (ابتدایی / متوسطه اول / متوسطه دوم)
//   • پایه‌های تحصیلی (۱ تا ۱۲)
//   • رشته‌های تحصیلی (ریاضی / تجربی / انسانی) — فقط ۱۰ تا ۱۲
//   • دروس هر پایه/رشته
//   • عنوان کتاب‌ها (با شماره در صورت وجود)
//   • normalize و lookup helpers
// ----------------------------------------------------------------
// بدون هیچ وابستگی بیرونی — قابل استفاده در کل وب اپ
// ================================================================

export const CURRICULUM_SCHEMA = '1.0.0';

// ================================================================
// SECTION 1: STAGES (مقاطع تحصیلی)
// ================================================================

export const STAGES = Object.freeze({
  PRIMARY: Object.freeze({
    id: 'primary',
    title: 'دوره ابتدایی — دبستان',
    shortTitle: 'ابتدایی',
    grades: Object.freeze(['1', '2', '3', '4', '5', '6']),
    order: 1,
  }),
  MIDDLE: Object.freeze({
    id: 'middle',
    title: 'دوره متوسطه اول',
    shortTitle: 'متوسطه اول',
    grades: Object.freeze(['7', '8', '9']),
    order: 2,
  }),
  HIGH: Object.freeze({
    id: 'high',
    title: 'دوره متوسطه دوم',
    shortTitle: 'متوسطه دوم',
    grades: Object.freeze(['10', '11', '12']),
    order: 3,
  }),
});

export const STAGE_LIST = Object.freeze(
  Object.values(STAGES).sort((a, b) => a.order - b.order)
);

// ================================================================
// SECTION 2: FIELDS (رشته‌ها — فقط برای ۱۰ تا ۱۲)
// ================================================================

export const FIELDS = Object.freeze({
  RIAZI: Object.freeze({
    id: 'riazi',
    title: 'ریاضی و فیزیک',
    shortTitle: 'ریاضی',
    aliases: Object.freeze(['ریاضی', 'ریاضی فیزیک', 'ریاضی و فیزیک', 'ریاضی-فیزیک', 'riazi']),
    order: 1,
  }),
  TAJROBI: Object.freeze({
    id: 'tajrobi',
    title: 'علوم تجربی',
    shortTitle: 'تجربی',
    aliases: Object.freeze(['تجربی', 'علوم تجربی', 'tajrobi']),
    order: 2,
  }),
  ENSANI: Object.freeze({
    id: 'ensani',
    title: 'علوم انسانی',
    shortTitle: 'انسانی',
    aliases: Object.freeze(['انسانی', 'علوم انسانی', 'ensani']),
    order: 3,
  }),
});

export const FIELD_LIST = Object.freeze(
  Object.values(FIELDS).sort((a, b) => a.order - b.order)
);

export const FIELD_IDS = Object.freeze(FIELD_LIST.map((f) => f.id));

// ================================================================
// SECTION 3: GRADES (پایه‌های تحصیلی)
// ================================================================

export const GRADES = Object.freeze({
  '1':  Object.freeze({ id: '1',  title: 'پایه اول',    shortTitle: 'اول',    stage: 'primary', order: 1,  hasFields: false,
    aliases: Object.freeze(['اول', 'یکم', 'پایه اول', '1']) }),
  '2':  Object.freeze({ id: '2',  title: 'پایه دوم',    shortTitle: 'دوم',    stage: 'primary', order: 2,  hasFields: false,
    aliases: Object.freeze(['دوم', 'پایه دوم', '2']) }),
  '3':  Object.freeze({ id: '3',  title: 'پایه سوم',    shortTitle: 'سوم',    stage: 'primary', order: 3,  hasFields: false,
    aliases: Object.freeze(['سوم', 'پایه سوم', '3']) }),
  '4':  Object.freeze({ id: '4',  title: 'پایه چهارم',  shortTitle: 'چهارم',  stage: 'primary', order: 4,  hasFields: false,
    aliases: Object.freeze(['چهارم', 'پایه چهارم', '4']) }),
  '5':  Object.freeze({ id: '5',  title: 'پایه پنجم',   shortTitle: 'پنجم',   stage: 'primary', order: 5,  hasFields: false,
    aliases: Object.freeze(['پنجم', 'پایه پنجم', '5']) }),
  '6':  Object.freeze({ id: '6',  title: 'پایه ششم',    shortTitle: 'ششم',    stage: 'primary', order: 6,  hasFields: false,
    aliases: Object.freeze(['ششم', 'پایه ششم', '6']) }),
  '7':  Object.freeze({ id: '7',  title: 'پایه هفتم',   shortTitle: 'هفتم',   stage: 'middle',  order: 7,  hasFields: false,
    aliases: Object.freeze(['هفتم', 'پایه هفتم', '7']) }),
  '8':  Object.freeze({ id: '8',  title: 'پایه هشتم',   shortTitle: 'هشتم',   stage: 'middle',  order: 8,  hasFields: false,
    aliases: Object.freeze(['هشتم', 'پایه هشتم', '8']) }),
  '9':  Object.freeze({ id: '9',  title: 'پایه نهم',    shortTitle: 'نهم',    stage: 'middle',  order: 9,  hasFields: false,
    aliases: Object.freeze(['نهم', 'پایه نهم', '9']) }),
  '10': Object.freeze({ id: '10', title: 'پایه دهم',    shortTitle: 'دهم',    stage: 'high',    order: 10, hasFields: true,
    aliases: Object.freeze(['دهم', 'پایه دهم', '10']) }),
  '11': Object.freeze({ id: '11', title: 'پایه یازدهم', shortTitle: 'یازدهم', stage: 'high',    order: 11, hasFields: true,
    aliases: Object.freeze(['یازدهم', 'پایه یازدهم', '11']) }),
  '12': Object.freeze({ id: '12', title: 'پایه دوازدهم',shortTitle: 'دوازدهم',stage: 'high',    order: 12, hasFields: true,
    aliases: Object.freeze(['دوازدهم', 'پایه دوازدهم', '12']) }),
});

export const GRADE_LIST = Object.freeze(
  Object.values(GRADES).sort((a, b) => a.order - b.order)
);

export const GRADE_IDS = Object.freeze(GRADE_LIST.map((g) => g.id));

// ================================================================
// SECTION 4: SUBJECTS (دروس)
// ================================================================
// ID پایدار — هرگز تغییر نمی‌کند. عنوان در UI با شماره‌ی کتاب غنی‌سازی می‌شود.

export const SUBJECTS = Object.freeze({
  // ---------- ابتدایی / مشترک ----------
  ghoran:       Object.freeze({ id: 'ghoran',       title: 'آموزش قرآن',       shortTitle: 'قرآن' }),
  farsi:        Object.freeze({ id: 'farsi',        title: 'فارسی',            shortTitle: 'فارسی' }),
  riazi:        Object.freeze({ id: 'riazi',        title: 'ریاضی',            shortTitle: 'ریاضی' }),
  olum:         Object.freeze({ id: 'olum',         title: 'علوم تجربی',       shortTitle: 'علوم' }),
  hedye:        Object.freeze({ id: 'hedye',        title: 'هدیه‌های آسمان',   shortTitle: 'هدیه‌ها' }),
  motaleat:     Object.freeze({ id: 'motaleat',     title: 'مطالعات اجتماعی',  shortTitle: 'مطالعات' }),

  // ---------- متوسطه اول ----------
  payam:        Object.freeze({ id: 'payam',        title: 'پیام‌های آسمان',   shortTitle: 'پیام‌ها' }),
  arabi:        Object.freeze({ id: 'arabi',        title: 'عربی، زبان قرآن',  shortTitle: 'عربی' }),
  english:      Object.freeze({ id: 'english',      title: 'زبان انگلیسی',     shortTitle: 'انگلیسی' }),

  // ---------- متوسطه دوم — مشترک ----------
  din:          Object.freeze({ id: 'din',          title: 'دین و زندگی',      shortTitle: 'دین و زندگی' }),

  // ---------- متوسطه دوم — ریاضی و فیزیک ----------
  hendese:      Object.freeze({ id: 'hendese',      title: 'هندسه',            shortTitle: 'هندسه' }),
  hesaban:      Object.freeze({ id: 'hesaban',      title: 'حسابان',           shortTitle: 'حسابان' }),
  amar_ehtemal: Object.freeze({ id: 'amar_ehtemal', title: 'آمار و احتمال',    shortTitle: 'آمار و احتمال' }),
  riazi_gosaste:Object.freeze({ id: 'riazi_gosaste',title: 'ریاضیات گسسته',    shortTitle: 'ریاضیات گسسته' }),
  fizik:        Object.freeze({ id: 'fizik',        title: 'فیزیک',            shortTitle: 'فیزیک' }),
  shimi:        Object.freeze({ id: 'shimi',        title: 'شیمی',             shortTitle: 'شیمی' }),

  // ---------- متوسطه دوم — علوم تجربی ----------
  zist:         Object.freeze({ id: 'zist',         title: 'زیست‌شناسی',       shortTitle: 'زیست' }),

  // ---------- متوسطه دوم — علوم انسانی ----------
  riazi_amar:   Object.freeze({ id: 'riazi_amar',   title: 'ریاضی و آمار',     shortTitle: 'ریاضی و آمار' }),
  olon_fonon:   Object.freeze({ id: 'olon_fonon',   title: 'علوم و فنون ادبی', shortTitle: 'علوم و فنون' }),
  tarikh:       Object.freeze({ id: 'tarikh',       title: 'تاریخ',            shortTitle: 'تاریخ' }),
  joghrafia:    Object.freeze({ id: 'joghrafia',    title: 'جغرافیا',          shortTitle: 'جغرافیا' }),
  jamee:        Object.freeze({ id: 'jamee',        title: 'جامعه‌شناسی',      shortTitle: 'جامعه‌شناسی' }),
  eqtesad:      Object.freeze({ id: 'eqtesad',      title: 'اقتصاد',           shortTitle: 'اقتصاد' }),
  manteq:       Object.freeze({ id: 'manteq',       title: 'منطق',             shortTitle: 'منطق' }),
  ravanshenasi: Object.freeze({ id: 'ravanshenasi', title: 'روان‌شناسی',       shortTitle: 'روان‌شناسی' }),
  falsafe:      Object.freeze({ id: 'falsafe',      title: 'فلسفه',            shortTitle: 'فلسفه' }),

  // ---------- متوسطه دوم — خاص ----------
  joghrafia_iran:Object.freeze({ id: 'joghrafia_iran',title: 'جغرافیای ایران', shortTitle: 'جغرافیای ایران' }),
  tarikh_moaser: Object.freeze({ id: 'tarikh_moaser', title: 'تاریخ معاصر ایران', shortTitle: 'تاریخ معاصر' }),
  zamin:         Object.freeze({ id: 'zamin',         title: 'زمین شناسی',      shortTitle: 'زمین شناسی' }),
  hoviat:        Object.freeze({ id: 'hoviat',        title: 'هویت اجتماعی',   shortTitle: 'هویت اجتماعی' }),
  salamat:       Object.freeze({ id: 'salamat',       title: 'سلامت و بهداشت', shortTitle: 'سلامت و بهداشت' }),

  // ---------- Deprecated (فقط برای سازگاری با داده‌های قدیمی) ----------
  tafakor:      Object.freeze({ id: 'tafakor',      title: 'تفکر و پژوهش',     shortTitle: 'تفکر',       deprecated: true }),
  kar:          Object.freeze({ id: 'kar',          title: 'کار و فناوری',     shortTitle: 'کار و فناوری', deprecated: true }),
});

export const SUBJECT_LIST = Object.freeze(
  Object.values(SUBJECTS).filter((s) => !s.deprecated)
);

export const SUBJECT_IDS = Object.freeze(SUBJECT_LIST.map((s) => s.id));

// ================================================================
// SECTION 5: CURRICULUM (نقشه‌ی دروس بر اساس پایه و رشته)
// ================================================================
//   grades 1-9  → CURRICULUM[gradeId] = [subjectId, ...]
//   grades 10-12 → CURRICULUM[gradeId] = { riazi: [...], tajrobi: [...], ensani: [...] }

export const CURRICULUM = Object.freeze({
  // ---------- دوره ابتدایی ----------
  '1': Object.freeze(['ghoran', 'farsi', 'riazi', 'olum']),
  '2': Object.freeze(['ghoran', 'hedye', 'farsi', 'riazi', 'olum']),
  '3': Object.freeze(['ghoran', 'hedye', 'farsi', 'riazi', 'olum', 'motaleat']),
  '4': Object.freeze(['ghoran', 'hedye', 'farsi', 'riazi', 'olum', 'motaleat']),
  '5': Object.freeze(['ghoran', 'hedye', 'farsi', 'riazi', 'olum', 'motaleat']),
  '6': Object.freeze(['ghoran', 'hedye', 'farsi', 'riazi', 'olum', 'motaleat']),

  // ---------- دوره متوسطه اول ----------
  '7': Object.freeze(['ghoran', 'payam', 'farsi', 'riazi', 'olum', 'motaleat',  'arabi', 'english']),
  '8': Object.freeze(['ghoran', 'payam', 'farsi', 'riazi', 'olum', 'motaleat',  'arabi', 'english']),
  '9': Object.freeze(['ghoran', 'payam', 'farsi', 'riazi', 'olum', 'motaleat',  'arabi', 'english']),

  // ---------- دوره متوسطه دوم ----------
  '10': Object.freeze({
    riazi:   Object.freeze(['farsi', 'din', 'arabi', 'english', 'riazi', 'hendese', 'fizik', 'shimi', 'joghrafia_iran']),
    tajrobi: Object.freeze(['farsi', 'din', 'arabi', 'english', 'riazi', 'zist', 'fizik', 'shimi', 'joghrafia_iran']),
    ensani:  Object.freeze(['farsi', 'din', 'arabi', 'english', 'riazi_amar', 'olon_fonon', 'tarikh', 'joghrafia_iran', 'jamee', 'eqtesad', 'manteq']),
  }),
  '11': Object.freeze({
    riazi:   Object.freeze(['farsi', 'din', 'arabi', 'english', 'hesaban', 'hendese', 'amar_ehtemal', 'fizik', 'shimi', 'tarikh_moaser']),
    tajrobi: Object.freeze(['farsi', 'din', 'arabi', 'english', 'riazi', 'zist', 'fizik', 'shimi', 'tarikh_moaser',"zamin"]),
    ensani:  Object.freeze(['farsi', 'din', 'arabi', 'english', 'riazi_amar', 'olon_fonon', 'tarikh', 'joghrafia', 'jamee', 'ravanshenasi', 'falsafe']),
  }),
  '12': Object.freeze({
    riazi:   Object.freeze(['farsi', 'din', 'arabi', 'english', 'hesaban', 'hendese', 'riazi_gosaste', 'fizik', 'shimi', 'hoviat', 'salamat']),
    tajrobi: Object.freeze(['farsi', 'din', 'arabi', 'english', 'riazi', 'zist', 'fizik', 'shimi', 'hoviat', 'salamat']),
    ensani:  Object.freeze(['farsi', 'din', 'arabi', 'english', 'riazi_amar', 'olon_fonon', 'tarikh', 'joghrafia', 'jamee', 'falsafe', 'hoviat', 'salamat']),
  }),
});

// ================================================================
// SECTION 6: BOOK NUMBERS (شماره‌ی کتاب — فقط متوسطه دوم)
// ================================================================
// کلید: `${gradeId}-${subjectId}` → مقدار: عدد فارسی
// عدم وجود کلید = کتاب شماره‌دار نیست (اقتصاد، منطق، …)

export const BOOK_NUMBERS = Object.freeze({
  // مشترک هر سه رشته
  '10-farsi': '۱',    '11-farsi': '۲',    '12-farsi': '۳',
  '10-din': '۱',      '11-din': '۲',      '12-din': '۳',
  '10-arabi': '۱',    '11-arabi': '۲',    '12-arabi': '۳',
  '10-english': '۱',  '11-english': '۲',  '12-english': '۳',

  // ریاضی (مشترک ریاضی و تجربی)
  '10-riazi': '۱',    '11-riazi': '۲',    '12-riazi': '۳',

  // ریاضی و فیزیک
  '10-hendese': '۱',  '11-hendese': '۲',  '12-hendese': '۳',
  '11-hesaban': '۱',  '12-hesaban': '۲',
  // amar_ehtemal و riazi_gosaste شماره ندارند

  // مشترک ریاضی و تجربی
  '10-fizik': '۱',    '11-fizik': '۲',    '12-fizik': '۳',
  '10-shimi': '۱',    '11-shimi': '۲',    '12-shimi': '۳',

  // علوم تجربی
  '10-zist': '۱',     '11-zist': '۲',     '12-zist': '۳',

  // علوم انسانی
  '10-riazi_amar': '۱', '11-riazi_amar': '۲', '12-riazi_amar': '۳',
  '10-olon_fonon': '۱', '11-olon_fonon': '۲', '12-olon_fonon': '۳',
  '10-tarikh': '۱',     '11-tarikh': '۲',     '12-tarikh': '۳',
  '11-joghrafia': '۲',  '12-joghrafia': '۳',
  '10-jamee': '۱',      '11-jamee': '۲',      '12-jamee': '۳',
  '11-falsafe': '۱',    '12-falsafe': '۲',
  // eqtesad، manteq، ravanshenasi شماره ندارند
});

// ================================================================
// SECTION 7: NORMALIZERS
// ================================================================

const PERSIAN_DIGITS = Object.freeze({
  '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
  '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
});

function toEnglishDigits(str) {
  return String(str == null ? '' : str).replace(/[۰-۹]/g, (d) => PERSIAN_DIGITS[d] || d);
}

/**
 * نرمال‌سازی شناسه‌ی پایه
 * ورودی‌های قابل قبول: '10' | 'پایه دهم' | 'دهم' | 'یازدهم' | 'پایه یازدهم' | '۱۰'
 * @returns {string|null} gradeId مثل '10' یا null
 */
export function normalizeGradeId(input) {
  if (input == null) return null;
  const raw = toEnglishDigits(String(input).trim());
  if (!raw) return null;

  // ۱. مستقیم بر اساس ID
  if (GRADES[raw]) return raw;

  const lower = raw.toLowerCase();

  // ۲. تطبیق دقیق با alias
  for (const g of GRADE_LIST) {
    for (const a of g.aliases) {
      if (a.toLowerCase() === lower) return g.id;
    }
  }

  // ۳. تطبیق زیررشته‌ای — طولانی‌ترین alias برنده
  //    (رفع باگ «یازدهم شامل دهم»)
  let bestId = null;
  let bestLen = 0;
  for (const g of GRADE_LIST) {
    for (const a of g.aliases) {
      const al = a.toLowerCase();
      if (lower.includes(al) && al.length > bestLen) {
        bestId = g.id;
        bestLen = al.length;
      }
    }
  }
  return bestId;
}

/**
 * نرمال‌سازی شناسه‌ی رشته
 * ورودی‌های قابل قبول: 'riazi' | 'ریاضی' | 'تجربی' | 'علوم انسانی' | 'انسانی'
 * @returns {string|null} fieldId یا null
 */
export function normalizeFieldId(input) {
  if (input == null) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  if (FIELDS[raw.toUpperCase()]) return FIELDS[raw.toUpperCase()].id;
  if (FIELD_IDS.includes(raw)) return raw;
  for (const f of FIELD_LIST) {
    for (const a of f.aliases) {
      if (a === raw || a.toLowerCase() === raw.toLowerCase()) return f.id;
    }
  }
  return null;
}

/**
 * نرمال‌سازی شناسه‌ی درس
 * ورودی‌های قابل قبول: 'farsi' | 'فارسی' | 'زیست' | 'زیست‌شناسی' | …
 * @returns {string|null} subjectId یا null
 */
export function normalizeSubjectId(input) {
  if (input == null) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  if (SUBJECTS[raw]) return raw;
  for (const s of Object.values(SUBJECTS)) {
    if (s.title === raw || s.shortTitle === raw) return s.id;
  }
  return null;
}

// ================================================================
// SECTION 8: LOOKUPS
// ================================================================

export function getGrade(gradeId) {
  const id = normalizeGradeId(gradeId);
  return id ? GRADES[id] : null;
}

export function getField(fieldId) {
  const id = normalizeFieldId(fieldId);
  if (!id) return null;
  return FIELD_LIST.find((f) => f.id === id) || null;
}

export function getSubject(subjectId) {
  const id = normalizeSubjectId(subjectId);
  return id ? SUBJECTS[id] : null;
}

export function getStageForGrade(gradeId) {
  const g = getGrade(gradeId);
  if (!g) return null;
  return STAGE_LIST.find((s) => s.id === g.stage) || null;
}

export function isHighSchool(gradeId) {
  const g = getGrade(gradeId);
  return !!(g && g.hasFields);
}

export function needsField(gradeId) {
  return isHighSchool(gradeId);
}

export function getFieldsForGrade(gradeId) {
  return isHighSchool(gradeId) ? FIELD_LIST.slice() : [];
}

// ================================================================
// SECTION 9: SUBJECT QUERIES
// ================================================================

export function getSubjectIdsFor(gradeId, fieldId = null) {
  const g = getGrade(gradeId);
  if (!g) return [];
  const entry = CURRICULUM[g.id];
  if (!entry) return [];

  // متوسطه اول و ابتدایی — آرایه ساده
  if (Array.isArray(entry)) return entry.slice();

  // متوسطه دوم — نیاز به رشته
  const f = normalizeFieldId(fieldId);
  if (!f) return [];
  return Array.isArray(entry[f]) ? entry[f].slice() : [];
}

export function getSubjectsFor(gradeId, fieldId = null) {
  return getSubjectIdsFor(gradeId, fieldId)
    .map((id) => SUBJECTS[id])
    .filter(Boolean);
}

export function getSubjectTitle(gradeId, subjectId) {
  const g = getGrade(gradeId);
  const s = getSubject(subjectId);
  if (!s) return '';
  if (!g) return s.title;
  const num = BOOK_NUMBERS[`${g.id}-${s.id}`];
  return num ? `${s.title} (${num})` : s.title;
}

export function getBookTitle(gradeId, fieldId, subjectId) {
  const g = getGrade(gradeId);
  const s = getSubject(subjectId);
  if (!g || !s) return '';
  const base = getSubjectTitle(g.id, s.id);
  if (g.hasFields) {
    const f = getField(fieldId);
    if (f) return `${base} — ${g.title} (${f.shortTitle})`;
  }
  return `${base} — ${g.title}`;
}

// ================================================================
// SECTION 10: UI HELPERS
// ================================================================

export function getGradeOptions({ includeAll = false, allLabel = 'همه پایه‌ها' } = {}) {
  const opts = GRADE_LIST.map((g) => ({ value: g.id, label: g.title, group: g.stage }));
  if (includeAll) opts.unshift({ value: '', label: allLabel, group: null });
  return opts;
}

export function getGradeOptionsGrouped() {
  return STAGE_LIST.map((stage) => ({
    stageId: stage.id,
    stageTitle: stage.title,
    options: stage.grades.map((gid) => ({
      value: gid,
      label: GRADES[gid].title,
    })),
  }));
}

export function getFieldOptions(gradeId, { includeEmpty = true, emptyLabel = 'انتخاب رشته…' } = {}) {
  if (!isHighSchool(gradeId)) return [];
  const opts = FIELD_LIST.map((f) => ({ value: f.id, label: f.title }));
  if (includeEmpty) opts.unshift({ value: '', label: emptyLabel });
  return opts;
}

export function getSubjectOptions(gradeId, fieldId = null, { includeAll = false, allLabel = 'همه دروس' } = {}) {
  const opts = getSubjectsFor(gradeId, fieldId).map((s) => ({
    value: s.id,
    label: getSubjectTitle(gradeId, s.id),
  }));
  if (includeAll) opts.unshift({ value: '', label: allLabel });
  return opts;
}

// ================================================================
// SECTION 11: BACKWARD COMPATIBILITY
// ================================================================
// سازگاری با کد قدیمی — همان نام‌ها، همان رفتار

export const GRADE_TITLES = Object.freeze(
  GRADE_LIST.reduce((acc, g) => { acc[g.id] = g.title; return acc; }, {})
);

export const SUBJECT_LABELS = Object.freeze(
  Object.values(SUBJECTS).reduce((acc, s) => { acc[s.id] = s.title; return acc; }, {})
);

// نقشه‌ی دروس برای پایه‌های ۱ تا ۹ (بدون رشته)
export const SUBJECTS_BY_GRADE = Object.freeze(
  Object.freeze(['1', '2', '3', '4', '5', '6', '7', '8', '9']).reduce((acc, gid) => {
    acc[gid] = getSubjectIdsFor(gid);
    return acc;
  }, {})
);

// نقشه‌ی دروس برای پایه‌های ۱۰ تا ۱۲ (با رشته)
export const SUBJECTS_BY_GRADE_AND_FIELD = Object.freeze(
  Object.freeze(['10', '11', '12']).reduce((acc, gid) => {
    acc[gid] = Object.freeze({
      riazi:   getSubjectIdsFor(gid, 'riazi'),
      tajrobi: getSubjectIdsFor(gid, 'tajrobi'),
      ensani:  getSubjectIdsFor(gid, 'ensani'),
    });
    return acc;
  }, {})
);

// ================================================================
// SECTION 12: DEFAULT EXPORT
// ================================================================

const curriculum = {
  SCHEMA: CURRICULUM_SCHEMA,

  // Constants
  STAGES, STAGE_LIST,
  FIELDS, FIELD_LIST, FIELD_IDS,
  GRADES, GRADE_LIST, GRADE_IDS,
  SUBJECTS, SUBJECT_LIST, SUBJECT_IDS,
  CURRICULUM, BOOK_NUMBERS,

  // Normalizers
  normalizeGradeId,
  normalizeFieldId,
  normalizeSubjectId,

  // Lookups
  getGrade, getField, getSubject,
  getStageForGrade,
  isHighSchool, needsField,
  getFieldsForGrade,

  // Subject queries
  getSubjectIdsFor,
  getSubjectsFor,
  getSubjectTitle,
  getBookTitle,

  // UI helpers
  getGradeOptions,
  getGradeOptionsGrouped,
  getFieldOptions,
  getSubjectOptions,

  // Backward compat
  GRADE_TITLES,
  SUBJECT_LABELS,
  SUBJECTS_BY_GRADE,
  SUBJECTS_BY_GRADE_AND_FIELD,
};

export default curriculum;